import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useAccesAdmin } from './acces';
import { adminFetch, apiErrorMessage, errorMessage } from './adminFetch';

interface Membre {
  id: string;
  email: string;
  nom: string | null;
  statut: string;
  superadmin: boolean;
  support: boolean;
  derniereConnexion: string | null;
  pret: boolean;
}

const DATE = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });

const JSON_ENTETES = { 'Content-Type': 'application/json' };

/**
 * Équipe de la SUITE : superadmins et agents support (27/09/2026).
 *
 * Réservé au superadmin. Les deux statuts valent pour toute la suite et
 * voyagent dans le jeton ; les rôles propres à un produit (FISCAL_ADMIN) se
 * donnent, eux, dans le back-office du produit (écran « Équipe »).
 */
export function AdminEquipePage() {
  const acces = useAccesAdmin();
  const [membres, setMembres] = useState<Membre[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const res = await adminFetch('/admin/equipe');
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setMembres((await res.json()) as Membre[]);
    } catch (err) {
      setErreur(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (!acces.superadmin) return <Navigate to="/admin/tickets" replace />;

  async function modifier(membre: Membre, changement: { superadmin?: boolean; support?: boolean }) {
    setErreur(null);
    setOccupe(membre.id);
    try {
      const res = await adminFetch(`/admin/equipe/${membre.id}`, {
        method: 'PATCH',
        headers: JSON_ENTETES,
        body: JSON.stringify(changement),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const maj = (await res.json()) as Membre;
      setMembres((liste) =>
        (liste ?? [])
          .map((m) => (m.id === maj.id ? maj : m))
          .filter((m) => m.superadmin || m.support),
      );
    } catch (err) {
      setErreur(errorMessage(err));
    } finally {
      setOccupe(null);
    }
  }

  async function nommer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErreur(null);
    const formulaire = event.currentTarget;
    const donnees = new FormData(formulaire);
    setOccupe('nouveau');
    try {
      const res = await adminFetch('/admin/equipe', {
        method: 'POST',
        headers: JSON_ENTETES,
        body: JSON.stringify({
          email: String(donnees.get('email') ?? ''),
          superadmin: donnees.get('superadmin') === 'on',
          support: donnees.get('support') === 'on',
        }),
      });
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      formulaire.reset();
      await charger();
    } catch (err) {
      setErreur(errorMessage(err));
    } finally {
      setOccupe(null);
    }
  }

  return (
    <div className="adm-page">
      <h1>Équipe de la suite</h1>
      <p className="adm-sub">
        Superadmins et agents support valent pour <strong>toute la suite</strong> : les produits
        les lisent dans le jeton de connexion. Un retrait prend effet ici immédiatement, et dans
        les produits au plus tard quinze minutes après. Les rôles propres à un produit
        (FISCAL_ADMIN) se donnent dans l’écran « Équipe » du back-office de ce produit.
      </p>

      {erreur && (
        <p className="adm-erreur" role="alert">
          {erreur}
        </p>
      )}

      <form className="adm-ajout" onSubmit={(e) => void nommer(e)}>
        <label>
          Adresse e-mail du compte
          <input name="email" type="email" required placeholder="prenom.nom@exemple.fr" />
        </label>
        <label className="adm-case">
          <input name="support" type="checkbox" defaultChecked /> Agent support
        </label>
        <label className="adm-case">
          <input name="superadmin" type="checkbox" /> Superadmin
        </label>
        <button className="btn" type="submit" disabled={occupe === 'nouveau'}>
          Ajouter
        </button>
      </form>
      <p className="adm-hint">
        La personne doit déjà avoir un compte sur le portail, adresse confirmée. Pour entrer, il lui
        faudra un mot de passe et la double authentification (colonne « Prêt »).
      </p>

      {membres === null ? (
        <p>Chargement…</p>
      ) : (
        <table className="adm-table">
          <thead>
            <tr>
              <th>Compte</th>
              <th>Superadmin</th>
              <th>Agent support</th>
              <th>Prêt</th>
              <th>Dernière connexion</th>
            </tr>
          </thead>
          <tbody>
            {membres.map((m) => (
              <tr key={m.id}>
                <td>
                  <strong>{m.email}</strong>
                  {m.nom ? <span className="adm-muted"> — {m.nom}</span> : null}
                  {m.email === acces.email ? <span className="adm-muted"> (vous)</span> : null}
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Superadmin : ${m.email}`}
                    checked={m.superadmin}
                    disabled={occupe === m.id}
                    onChange={(e) => void modifier(m, { superadmin: e.target.checked })}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Agent support : ${m.email}`}
                    checked={m.support}
                    disabled={occupe === m.id}
                    onChange={(e) => void modifier(m, { support: e.target.checked })}
                  />
                </td>
                <td>{m.pret ? 'Oui' : <span className="adm-alerte">Non (mot de passe ou TOTP manquant)</span>}</td>
                <td>{m.derniereConnexion ? DATE.format(new Date(m.derniereConnexion)) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
